import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { BossKeyBridge } from './components/BossKeyBridge';
import { PasswordGate } from './pages/Login';
import { Home } from './pages/Home/index';
import { Editor } from './pages/Editor/index';
import { Reader } from './pages/reader';
import { HarnessDemo } from './pages/harness-demo';
import { MobilePage } from './pages/mobile/MobilePage';
import { BootProvider } from './cache/BootContext';
import { applyAutoUiZoom } from './utils/uiScale';

/**
 * 整体界面缩放：启动时算一次（`web-entry.tsx` 里在首帧前已经算过），
 * 之后**窗口尺寸变化时逐帧同步重算**。
 *
 * ⚠️ 这里**不能防抖**（踩过）：防抖会让"窗口已经变宽、缩放还是旧值"持续一段时间，
 * 于是布局空间 `视口宽 / 缩放` 被算大、界面短暂地比最终状态更宽松 ——
 * 表现就是编辑区顶部工具栏**拖动时是一行、松手后又变回两行**（用户实测反馈）。
 * 同步重算的代价很低：系数保留两位小数（`computeAutoUiScale`），
 * 且 `applyUiZoom` 值没变就不碰 DOM，所以一次拖动通常只写几次样式。
 *
 * 规则与理由见 `utils/uiScale.ts`：系数 = min(宽/设计宽, 高/设计高)，夹在 0.7~1.25；
 * 用户在外观设置里选的那一档是**乘在它之上的偏好**。
 */
function useAutoUiZoom() {
  useEffect(() => {
    const onResize = () => { applyAutoUiZoom(); };
    applyAutoUiZoom();
    window.addEventListener('resize', onResize);
    window.addEventListener('hashchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('hashchange', onResize);
    };
  }, []);
}

function ResponsiveContainer({ children }: { children: React.ReactNode }) {
  useAutoUiZoom();
  return (
    <div style={{ width: '100%', height: 'calc(100vh / var(--ui-zoom, 1))', overflow: 'hidden' }}>
      {children}
    </div>
  );
}

/**
 * 启动时的登录门禁。
 *
 * `sessionStorage.sw_authenticated` 只是"本标签页已经登录过"的**缓存**（按标签页存活）。
 * 登录态的真相在服务端的 `sw_session` Cookie 里，而它是**浏览器会话 Cookie**：
 * 关掉浏览器（含关机）即失效，下次打开必须重新输密码 —— 这是刻意的，
 * 私人写作工具不该"登录一次、之后一周内开机就免密进主页"。
 *
 * 因此这里**挂载时总是向服务端复核一次**（`GET /api/auth/session`，走 authMiddleware）：
 *  - 缓存命中 → 先照常渲染（不闪白屏），但复核失败时立刻清缓存并赶去输密码；
 *  - 缓存缺失 → 探测期间先不渲染（返回 null），避免每次冷启动闪一下密码页。
 * 只信缓存是不行的：浏览器的"继续上次浏览 / 崩溃恢复"会把 sessionStorage 一起恢复回来，
 * 而 Cookie 可能早就没了（关过浏览器、或空闲超时）。
 *
 * 被拦下时把**原本要去的路由**带进 `?next=`，登录后回到原处 —— 否则重启浏览器后
 * 只会落到书架，而"有 N 项待确认…前往处理"的提醒只在编辑器里扫（见 WritingMode），
 * 等于把"AI 还在等你拍板"藏了起来。
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<'checking' | 'ok' | 'no'>(() =>
    (sessionStorage.getItem('sw_authenticated') === '1' ? 'ok' : 'checking'));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/auth/session', { credentials: 'include' });
        if (cancelled) return;
        if (res.ok) {
          sessionStorage.setItem('sw_authenticated', '1');
          setState('ok');
        } else {
          // 401：Cookie 已失效（关过浏览器 / 空闲超时 / 改过密码）→ 清掉缓存，
          // 否则它会让下一次冷启动先渲染出书架、再被 401 赶走。
          sessionStorage.removeItem('sw_authenticated');
          setState('no');
        }
      } catch {
        // 网络失败：本来就没有缓存凭据时按未登录处理（保守）；
        // 已有缓存时不因一次抖动把人踢到登录页 —— 后续请求的 401 会处理。
        if (!cancelled && sessionStorage.getItem('sw_authenticated') !== '1') setState('no');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === 'checking') return null;
  if (state === 'no') {
    // 带上"原本要去哪"：登录页读取 ?next= 并在登录成功后跳回去
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <ResponsiveContainer>
      <HashRouter>
        <BootProvider>
          {/* 老板键：把组合下发给原生键盘钩子（识别在 Rust 侧，见 bosskey.rs） */}
          <BossKeyBridge />
          <Routes>
            <Route path="/" element={<PasswordGate />} />
            <Route path="/home" element={<AuthGuard><Home /></AuthGuard>} />
            <Route path="/editor/:id" element={<AuthGuard><Editor /></AuthGuard>} />
            <Route path="/reader/:id" element={<AuthGuard><Reader /></AuthGuard>} />
            <Route path="/harness" element={<AuthGuard><HarnessDemo /></AuthGuard>} />
            <Route path="/m" element={<MobilePage />} />
            <Route path="/studio" element={<Navigate to="/home" replace />} />
            <Route path="/studio/:bookId" element={<Navigate to="/home" replace />} />
          </Routes>
        </BootProvider>
      </HashRouter>
    </ResponsiveContainer>
  );
}
